/**
 * Chatfenster: Verlauf plus Eingabezeile. Ein Baustein für Lobby- und
 * Partie-Chat gleichermaßen – beide unterscheiden sich nur im Kanalnamen.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Notice, Spinner } from './ui';
import { useAuth } from '../store/auth';
import { useChat } from '../store/chat';

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export function ChatPanel({ channel, compact }: { channel: string; compact?: boolean }) {
  const { messages, loading, error, open, close, send } = useChat();
  const [draft, setDraft] = useState('');
  const currentUser = useAuth((s) => s.user);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void open(channel);
    return () => close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const submit = () => {
    if (!draft.trim()) return;
    send(draft);
    setDraft('');
  };

  return (
    <div className="flex flex-col" style={{ height: compact ? '20rem' : '32rem' }}>
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-1 py-2">
        {loading && (
          <div className="flex items-center gap-2 text-xs text-panel-500 dark:text-panel-400">
            <Spinner className="h-3 w-3" /> Verlauf wird geladen…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="py-6 text-center text-xs text-panel-500 dark:text-panel-400">Noch keine Nachrichten. Schreib die erste.</p>
        )}
        {messages.map((m) => {
          const isSelf = m.authorId === currentUser?.id;
          return (
            <div key={m.id} className="text-sm">
              <span className={isSelf ? 'font-semibold text-accent-600 dark:text-accent-400' : 'font-semibold'}>{m.displayName}</span>{' '}
              <span className="text-2xs text-panel-400">{timeLabel(m.createdAt)}</span>
              <p className="break-words text-panel-800 dark:text-panel-100">{m.body}</p>
            </div>
          );
        })}
      </div>

      {error && <Notice tone="danger">{error}</Notice>}

      <form
        className="mt-2 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Nachricht schreiben…"
            maxLength={500}
          />
        </div>
        <Button type="submit" variant="primary" disabled={!draft.trim()}>
          Senden
        </Button>
      </form>
    </div>
  );
}
